import { ETH_ADDRESS } from '@kwenta/sdk/constants'
import CheckIcon from 'assets/svg/app/check-fill.svg'
import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import Spacer from 'components/Spacer'
import { PROVIDER_MAINNET_CHAINS } from 'constants/network'
import useNetworkSwitcher from 'hooks/useNetworkSwitcher'
import dynamic from 'next/dynamic'
import { type FC, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { selectSubmittingFuturesTx } from 'state/futures/selectors'
import {
	selectCrossMarginAvailableMargin,
	selectMarginEnginePermitted,
	selectMargineEngineEnabled,
	selectSnxV3Account,
} from 'state/futures/snxPerpsV3/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import styled from 'styled-components'

import { wei } from '@kwenta/wei'
import ProgressIndicator from 'components/ProgressIndicator'
import { Body, Heading } from 'components/Text'
import { FlexDivColCentered } from 'components/layout/flex'
import { FlexDivCol } from 'components/layout/flex'
import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import { selectEthWalletBalance } from 'state/balances/selectors'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import { createPerpsV3Account, grantMarginEnginePermission } from 'state/futures/snxPerpsV3/actions'
import media from 'styles/media'
import DepositCrossMargin from './DepositWithdraw/DepositCrossMargin'

const SocketBridge = dynamic(() => import('../../components/SocketBridge'), {
	ssr: false,
})

interface Props {
	onDismiss: () => void
}

const CrossMarginOnboarding: FC<Props> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { trackAccountCreation } = useAnalytics()

	const txProcessing = useAppSelector(selectSubmittingFuturesTx)
	const availableMargin = useAppSelector(selectCrossMarginAvailableMargin)
	const marginEngineEnabled = useAppSelector(selectMargineEngineEnabled)
	const account = useAppSelector(selectSnxV3Account)
	const marginEnginePermitted = useAppSelector(selectMarginEnginePermitted)
	const ethBalance = useAppSelector(selectEthWalletBalance)

	const provider = useAppSelector(selectPerpsProvider)
	const { switchToL2 } = useNetworkSwitcher(PROVIDER_MAINNET_CHAINS[provider])
	const [skipDeposit, setSkipDeposit] = useState(false)

	const createAccount = useCallback(() => {
		trackAccountCreation(EVENT_NAME.SUBMIT_CREATE_ACCOUNT)
		dispatch(createPerpsV3Account())
	}, [dispatch, trackAccountCreation])

	const totalSteps = marginEngineEnabled ? 4 : 3

	const step = useMemo(() => {
		if (wei(availableMargin).gt(0) || skipDeposit) {
			// Complete
			return totalSteps
		} else if (!!account && (marginEnginePermitted || !marginEngineEnabled)) {
			// Deposit
			return totalSteps - 1
		} else if (marginEngineEnabled && !!account) {
			// Permit margin engine
			return 2
		}
		// Create account
		return 1
	}, [
		availableMargin,
		account,
		skipDeposit,
		marginEnginePermitted,
		totalSteps,
		marginEngineEnabled,
	])

	const showBridge = useMemo(() => {
		return wei(ethBalance).eq(0)
	}, [ethBalance])

	const content = useMemo(() => {
		if (step === 1) {
			return (
				<>
					<StyledBody color="secondary">
						{showBridge
							? t('futures.modals.onboard.step1-bridge')
							: t('futures.modals.onboard.step1-intro')}
					</StyledBody>
					{showBridge ? (
						<BridgeContainer>
							<SocketBridge defaultToken={ETH_ADDRESS} onSuccess={() => switchToL2()} />
						</BridgeContainer>
					) : (
						<TransactionButton
							fullWidth
							variant="solid-highlight"
							size="small"
							data-testid="cross-margin-onboard-create-account"
							onClick={createAccount}
							loading={txProcessing}
							disabled={txProcessing}
						>
							{t('futures.modals.onboard.create-account')}
						</TransactionButton>
					)}
				</>
			)
		}

		if (step === 2 && marginEngineEnabled) {
			return (
				<>
					<StyledBody color="secondary">{t('futures.modals.onboard.step2-intro-cm')}</StyledBody>
					<TransactionButton
						fullWidth
						variant="solid-highlight"
						size="small"
						loading={txProcessing}
						disabled={txProcessing}
						data-testid="cross-margin-onboard-add-delegate"
						onClick={() => {
							dispatch(grantMarginEnginePermission())
						}}
					>
						{t('futures.modals.onboard.permit-margin-engine')}
					</TransactionButton>
				</>
			)
		}

		if (step === 3) {
			return (
				<>
					<StyledBody color="secondary">{t('futures.modals.onboard.step3-intro-cm')}</StyledBody>
					<ButtonContainer rowGap="16px">
						<DepositCrossMargin buttonSize="small" />
						<TransactionButton
							fullWidth
							data-testid="cross-margin-onboard-deposit"
							size="small"
							onClick={() => setSkipDeposit(true)}
						>
							{t('futures.modals.onboard.skip')}
						</TransactionButton>
					</ButtonContainer>
				</>
			)
		}

		return (
			<>
				<FlexDivColCentered rowGap="12px">
					<StyledCircleCheckIcon height={60} width={60} />
					<FlexDivColCentered rowGap="8px">
						<Heading variant="h6">{t('account.create-account-tracker.account-created')}</Heading>
						<Body color="secondary">
							{t(
								`account.create-account-tracker.completed-copy${skipDeposit ? '-skipped-deposit' : ''}`
							)}
						</Body>
					</FlexDivColCentered>
				</FlexDivColCentered>
				<TransactionButton
					fullWidth
					variant="solid-highlight"
					size="small"
					data-testid="cross-margin-onboard-create-account"
					onClick={onDismiss}
				>
					{t('account.create-account-tracker.complete-setup')}
				</TransactionButton>
			</>
		)
	}, [
		dispatch,
		step,
		t,
		txProcessing,
		marginEngineEnabled,
		showBridge,
		skipDeposit,
		createAccount,
		onDismiss,
		switchToL2,
	])

	return (
		<StyledBaseModal onDismiss={onDismiss} isOpen title="" transparentOverlay={false}>
			<Container>
				<ProgressIndicator currentStep={step + 1} totalSteps={totalSteps + 1} />
				<Heading variant="h5">
					{step === 1
						? t('account.create-account-tracker.title')
						: step === 2
							? t('account.create-account-tracker.margin-engine')
							: step === 3
								? t('account.create-account-tracker.deposit-margin')
								: t('account.create-account-tracker.cm-account-created')}
				</Heading>
				<Divider height={1} divider />
				{content}
			</Container>
		</StyledBaseModal>
	)
})

const BridgeContainer = styled.div`
	border: 1px solid #2D2D38;
	border-radius: 10px;
	margin: 0 50px;
	margin-bottom: 10px;
`

const ButtonContainer = styled(FlexDivCol)`
	row-gap: 16px;
`

const StyledBody = styled(Body)`
	line-height: 1.5;
`

const Divider = styled(Spacer)`
	width: 100%;
`

const Container = styled(FlexDivCol)`
	row-gap: 30px;
	padding: 10px;
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		max-width: 550px;
		position: absolute;
		top: 45%;
		left: 50%;
		transform: translate(-50%, -50%);
		margin-top: 0;
	}

	> div:nth-child(2) {
		width: 550px;
	}

	${media.lessThan('md')`
		[data-reach-dialog-content] {
			max-width: 100%;
		}
	`}
`

const StyledCircleCheckIcon = styled(CheckIcon)`
	path {
		fill: ${(props) => props.theme.colors.green};
	}
`

export default CrossMarginOnboarding
